import Enumerable from "linq";
import * as idbEdit from "../../entity/idb/editInfo";
import * as es6promise from "es6-promise";
import { Logger } from "../../util/logger";
import { GetEditInfo, response, util } from "../..";
import { constDialogMessage, dialogUtil } from "../../util";
import { asyncWhenApply, calcCheckSum, editStrage } from "../edit.tool";
import { editController } from "../../edit";

import { EditConst, EDIT_MODE_KIND } from "../edit.const";
import { photoFrame, UploadImageResponseEntity, uploadPhoto } from "../edit.entity";
import { itemVenderProperty } from "../edit.sonbp_if";
import { BaseStampLogicClass } from "./edit.baseStampLogic";


/**
 * アップロード情報処理基底クラス
 * 画像アップロード、画像配置の処理を提供するクラス
 */
export abstract class UploadImageLogicClass extends BaseStampLogicClass {

// #region ■定数

    /**
     * アップロード画像最大数
     */
    protected readonly UPLOAD_IMAGE_NUM_MAX: number;
    /**
     * 画像アップロード(WebAPI)呼び出しURL
     */
    protected readonly WEBAPI_URL_UPLOADIMAGE: string;
    /**
     * 画像サムネイル取得(WebAPI)呼び出しURL
     */
    protected readonly WEBAPI_URL_GETTHUMBNAIL: string;
    /**
     * 写真固有の動作必要か
     */
    protected readonly _isPhotoFlg: boolean = false;

// #endregion

// #region ■変数

    /**
     * アップロードファイル一覧
     */
    public _uploadImageList: uploadPhoto[];

    // #endregion

// #region ■プロパティ

    /**
     * 空き枠数
     */
    public get EmptyUploadNum(): number {

        let count: number = 0;

        count = Enumerable.from(this._uploadImageList).where(row => row.isEmpty && !row.deleteFlg).count();

        return count;
    }

    /**
     * 使用済み枠数
     */
    public get NotEmptyUploadNum(): number {

        let count: number = 0;

        count = Enumerable.from(this._uploadImageList).where(row => !row.isEmpty && !row.deleteFlg).count();

        return count;
    }

    /**
     * アップロード画像最大数
     * @abstract
     */
    abstract UploadNumMax(this: UploadImageLogicClass): number;

// #endregion

// #region ■起動

    /**
     * コンストラクタ
     */
    constructor(edctrl: editController, uploadList: uploadPhoto[]) {
        super(edctrl);
        this._uploadImageList = uploadList;
    }

    /**
     * アップロード情報の復元
     */
    public restoreUoloadList(isFromIdb: boolean, isUsed: boolean, idbList: idbEdit.photo[], editInfoList: GetEditInfo.GetEditInfoPhotoEntity[]) {

        // アップロード情報の復元
        let rowIdx: number = 0;
        if (isFromIdb && idbList) {
            // プレビュー画面から戻ってきた,F5の場合はindexDBからの復元
            Logger.debug("アップロード画像一覧 indexDBからの復元", idbList);

            for (let i: number = 0; i < idbList.length; i++) {
                if (idbList[i].fileId) {
                    const ulp = new uploadPhoto();
                    ulp.key = i.toString();
                    ulp.fileId = idbList[i].fileId;
                    ulp.isUsed = isUsed ? idbList[i].isUsed : false;;
                    ulp.width = idbList[i].width;
                    ulp.height = idbList[i].height;
                    ulp.uploadDate = idbList[i].uploadDate
                    ulp.fileName = idbList[i].fileName;
                    ulp.isEmpty = false;
                    ulp.fileKey = "";

                    // photo only.
                    ulp.fileKey = idbList[i].fileNameKey ? idbList[i].fileNameKey : "";
                    ulp.deleteDate = idbList[i].deleteDate ? idbList[i].deleteDate : undefined;
                    ulp.deleteFlg = idbList[i].deleteFlg ? idbList[i].deleteFlg : false;

                    this._uploadImageList.push(ulp);
                    rowIdx++;
                }
            }
        } else {
            // その他の場合はDBからの復元
            Logger.debug("アップロード画像一覧 DBからの復元", editInfoList);

            // DBからの復元
            rowIdx = (this.UploadNumMax() < editInfoList.length) ? this.UploadNumMax() : editInfoList.length;
            for (let i: number = 0; i < rowIdx; i++) {
                const ulp = new uploadPhoto();
                ulp.key = i.toString();
                ulp.fileId = editInfoList[i].fileId;
                ulp.isUsed = isUsed ? editInfoList[i].isUsed : false;
                ulp.width = editInfoList[i].width;
                ulp.height = editInfoList[i].height;
                ulp.uploadDate = editInfoList[i].uploadDate
                ulp.fileName = editInfoList[i].uploadFileName;
                ulp.isEmpty = false;
                ulp.fileKey = "";
                this._uploadImageList.push(ulp);
            }
        }
        // アップロード一覧に最大数分の画像枠を補完
        for (let i: number = rowIdx; i < this.UploadNumMax(); i++) {
            const ulp = new uploadPhoto();
            ulp.key = i.toString();
            ulp.isUsed = false;
            ulp.isEmpty = true;
            this._uploadImageList.push(ulp);
        }
        Logger.debug("アップロード一覧の復元", this._uploadImageList);
    }

// #endregion

// #region ■イベント

    /**
     * 画像一覧→画像削除OKボタン押下メソッド
     * @summary 一覧の選択画像を削除し、削除選択モードを解除する。
     */
    public btnClickUploadListTrashOK(event: JQueryEventObject): void {

        if (!this._usePcDesign) {
            // SP版の場合は、selected から reqDeleteにコンバート
            Enumerable.from(this._uploadImageList)
                .where(row => row.selected === EditConst.IMG_SELECTED)
                .forEach(row => row.reqDelete = true);
        }

        // 削除対象の画像IDを抽出
        let removeIds = this.getRemoveTargetIds();

        Logger.log("画像一覧→画像削除OKボタン押下メソッド=>", removeIds);

        // 使用中の画像が指定されているか判定
        if (removeIds.length > 0) {
            // 確認メッセージ表示
            util.dialogUtil.createYesNoDialog(
                constDialogMessage.INFO_EDIT_DEL_PHOTO_CHECK,
                () => {
                    util.dialogUtil.closeYesNoDialog();
                    // 削除処理
                    this.requestRemove(removeIds);
                },
                util.dialogUtil.closeYesNoDialog,
                "", "",
                util.dialogUtil.DialogType.Infomation)
        }
        else {
            // 削除処理
            this.requestRemove(removeIds);
        }
    }

    /**
     * 画像押下メソッド
     * @summary PC/SPのディスパッチのみ
     */
    public imgClickUploadImage(event: JQueryEventObject, fileId: string): void {

        if (!fileId) {
            Logger.debug("fileId is undefined");
            return;
        }

        if (this._usePcDesign) {
            // PC用
            this.imgClickUploadImagePc(fileId);
        }
        else {
            // SP用
            this.imgClickUploadImageSp(fileId);
        }
    }

    /**
     * ファイル選択の状態変更イベント
     * @summary ファイル選択ダイアログからファイル選択されると呼ばれる。アップロードの準備開始する。
     */
    public onChangeUploadFileObj(event: any) {

        if (util.verify.isEmpty(event.target.files)) {
            return;
        }
        else if (event.target.files.length == 0) {
            return;
        }

        // ファイルオブジェクト取得
        const fileList: FileList = event.target.files;

        // ローディングダイアログ開始
        util.dialogUtil.showLoading();

        // アップロードファイルの最大数チェック
        if (this.UploadNumMax() < this.NotEmptyUploadNum + fileList.length) {
            // メッセージ表示
            util.dialogUtil.createErrorDialog(util.constDialogMessage.ERROR_IMAGE_UPLOAD_OVER_MAX_TEXT, () => {
                dialogUtil.closeErrorDialog();
                // ローディングダイアログ終了
                util.dialogUtil.closeLoading();
            }, dialogUtil.DialogType.Warning);
            return;
        }

        let validMsgList: string[] = [];
        let validFileList: File[] = [];
        for (let i: number = 0; i < fileList.length; i++) {
            // アップロード可能ファイルであるかのチェック
            const resultMsg: string = this.uploadFileValid(fileList[i]);
            if (resultMsg.length == 0) {
                validFileList.push(fileList[i]);
            }
            else {
                validMsgList.push(resultMsg);
            }
        }

        if (fileList.length === validMsgList.length) {
            // アップロード不可ファイルなしの場合
            let messageListTop5: string[] = getTop5Message(validMsgList);
            const msg: string = util.constDialogMessage.ERROR_IMAGE_ANALYZER_BASE_TEXT + "<br>" + messageListTop5.join("<br>");
            util.dialogUtil.createErrorDialog(msg, () => {
                // ローディングダイアログ終了
                util.dialogUtil.closeLoading();
                util.dialogUtil.closeErrorDialog();
            }, dialogUtil.DialogType.Warning);
            return;
        }

        // アップロード処理へ
        this._vue.$data.Show.FirstGuidance = false; // アップロード中に操作できないように一度外す
        this.uploadImageFileCover(validFileList).then((messageList: string[]) => {

            Logger.debug("アップロード処理後処理", messageList);

            if (0 < validMsgList.length) {
                for (let i: number = 0; i < validMsgList.length; i++) {
                    messageList.unshift(validMsgList[i]);
                }
            }

            if (messageList.length === 0) {
                if (this._isPhotoFlg && this.NotEmptyUploadNum == 0) {
                    // 1個も画像がアップできなければ再度firstGuidanceを設定
                    this._vue.$data.Show.FirstGuidance = true;
                }
                // ローディングダイアログ終了
                util.dialogUtil.closeLoading();
            }
            else {
                // 上記以外の場合
                let messageListTop5: string[] = getTop5Message(messageList);
                const msg: string = util.constDialogMessage.ERROR_IMAGE_ANALYZER_BASE_TEXT + "<br>" + messageListTop5.join("<br>");
                util.dialogUtil.createErrorDialog(msg, () => {
                    // ローディングダイアログ終了
                    util.dialogUtil.closeLoading();
                    if (this._isPhotoFlg && this.NotEmptyUploadNum == 0) {
                        this._vue.$data.Show.FirstGuidance = true;
                    }
                    dialogUtil.closeErrorDialog();
                }, dialogUtil.DialogType.Warning);
            }

            // ファイルオブジェクトクリア
            $(".upl_image_input").val("");

        }).catch((messageList: string[]) => {
            util.dialogUtil.closeLoading();
            if (messageList) {
                let messageListTop5: string[] = getTop5Message(messageList);
                const msg: string = util.constDialogMessage.ERROR_FILE_NG_TEXT + "<br>" + messageListTop5.join("<br>");
                util.dialogUtil.createErrorDialog(msg, () => {
                    if (this._isPhotoFlg && this.NotEmptyUploadNum == 0) {
                        // 1個でも画像がアップできなければ再度firstGuidanceを設定
                        this._vue.$data.Show.FirstGuidance = true;
                    }
                    dialogUtil.closeErrorDialog();
                }, dialogUtil.DialogType.Warning);
            }
            else {
                // こちらの遷移は基本的に fatal error
            }

            // ファイルオブジェクトクリア
            $(".upl_image_input").val("");
        });

        // 高さ不足を考慮して、Top5を抽出
        function getTop5Message(messageList: string[]): string[] {
            let messageListTop5: string[];

            if (5 < messageList.length) {
                messageListTop5 = [];
                messageListTop5 = messageList.slice(0, 5);
                messageListTop5.push("．．．");
            }
            else {
                messageListTop5 = messageList.slice();
            }

            return messageListTop5;
        }
    }


    // #region ■イベント（PCのみ処理）

    /**
     * 画像押下メソッド(PC用)
     * @summary クリックされた画像をCanvasへ配置する。削除モードの場合は削除選択フラグをONにする。
     */
    private imgClickUploadImagePc(fileId: string) {

        // モードによって動作が変わります(画像選択 or 画像削除)
        if (this._vue.$data.UploadListModeSelect) {
            Logger.debug("画像押下メソッド fileId[" + fileId + "] 画像選択モード");

            // 上限チェック
            let count = this.UseCount();
            if (count >= this.PUT_MAX) {
                dialogUtil.createErrorDialog(`配置可能な上限(${this.PUT_MAX}件)を超えました。`, dialogUtil.closeErrorDialog);
                return;
            }

            // 選択画像を設定
            this.setImageBody(fileId, null, null);
        }
        else {
            Logger.debug("画像押下メソッド fileId[" + fileId + "] 画像削除モード");

            Enumerable.from(this._uploadImageList)
                .where(row => row.fileId === fileId)
                .forEach(row => {
                    row.reqDelete = true;
                    row.selected = EditConst.IMG_SELECTED;
                });
            this.enableUploadListImageTrashOK();
        }
    }

    /**
     * 画像押下メソッド(削除モード時)
     * @summary クリックされた画像の削除選択フラグをOFFにする。
     */
    public imgClickUploadListImageCoverPc(event: JQueryEventObject, fileId: string): void {

        if (!this._vue.$data.UploadListModeSelect) {
            // 画像削除モード
            Enumerable.from(this._uploadImageList)
                .where(row => row.fileId === fileId)
                .forEach(row => {
                    row.reqDelete = false;
                    row.selected = EditConst.IMG_NOSELECT;
                });
            this.enableUploadListImageTrashOK();
        }


    }

    /**
     * 画像一覧→画像削除モード切り替えボタン押下メソッド
     * @summary 一覧を削除選択モードにする。
     */
    public btnClickUploadListTrashPc(event: JQueryEventObject): void {
        this._vue.$data.UploadListModeSelect = false;
    }

    /**
     * 画像一覧→画像削除キャンセルボタン押下メソッド
     * @summary 一覧の削除選択モードを解除して配置選択モードにする。
     */
    public btnClickUploadListTrashCancelPc(event: JQueryEventObject): void {

        this._vue.$data.UploadListModeSelect = true;

        // 画像削除アイコンOFF
        this._uploadImageList.forEach(row => {
            row.reqDelete = false;
            row.selected = EditConst.IMG_NOSELECT;
        });
    }

    /**
     * 画像一覧→切抜き編集モードキャンセルボタン押下メソッド（マイスタンプ用）
     * @summary 切抜き編集選択モードを解除して配置選択モードにする。
     */
    public btnClickUploadListMystampEditCancelPc(event: JQueryEventObject): void {
        this._vue.$data.UploadListModeSelect = true;
    }

    // #endregion

    // #region ■イベント（SPのみ処理）

    /**
     * 画像押下メソッド(SP用)
     * @summary クリックされた画像を選択状態（配置or削除候補）にする。
     */
    private imgClickUploadImageSp(fileId: string) {
        Logger.debug("画像押下メソッド fileId[" + fileId + "]");

        // 対象画像の現状態を取得
        const selImage: uploadPhoto = util.relic.listFind(this._uploadImageList, (p: uploadPhoto) => {
            return p.fileId === fileId;
        });
        if (!selImage) {
            return;
        }
        const targetSelected: string = selImage.selected === EditConst.IMG_SELECTED ? EditConst.IMG_NOSELECT : EditConst.IMG_SELECTED;
        const otherSelected: string = EditConst.IMG_NOSELECT;

        for (let i: number = 0; i < this._uploadImageList.length; i++) {
            if (this._uploadImageList[i].fileId === fileId) {
                this._uploadImageList[i].selected = targetSelected;
            }
            else {
                this._uploadImageList[i].selected = otherSelected;
            }
        }

        // 削除(＆次へ)ボタンの活性制御
        this._vue.$data.EnableUploadListTrashOK = targetSelected === EditConst.IMG_SELECTED ? true : false;
    }

    /**
     * (画像一覧から)次へボタン押下(SP)
     * @summary 選択画像をCanvasへ配置し、位置調整画面（写真ならメイン画面）へ遷移する。
     */
    public btnClickNextFromUploadImageListSp(event: JQueryEventObject): void {

        Logger.debug("(画像一覧から)次へボタン押下(SP)");

        // 選択画像取得
        const selPhoto: uploadPhoto = util.relic.listFind(this._uploadImageList, (photo: uploadPhoto) => {
            return photo.selected === EditConst.IMG_SELECTED;
        }, true);
        if (!selPhoto) {
            Logger.debug("画像が選択されていません");
            return;
        }

        // 上限チェック
        let count = this.UseCount();
        if (count >= this.PUT_MAX) {
            dialogUtil.createErrorDialog(`配置可能な上限(${this.PUT_MAX}件)を超えました。`, dialogUtil.closeErrorDialog);
            return;
        }

        // 画像設定
        this.setImageBody(selPhoto.fileId, selPhoto, () => {
            // 選択状態を全解除
            for (let i: number = 0; i < this._uploadImageList.length; i++) {
                this._uploadImageList[i].selected = EditConst.IMG_NOSELECT;
            }

            // 次の画面へ
            if (this._isPhotoFlg) {
                this._edctrl.btnClickReturnHome(false, false, null);
            } else {
                if (!this._usePcDesign) {
                    // SP用
                    this.returnStampHomeBtn(null);
                }
            }
        });

        // 選択状態を全解除
        for (let i: number = 0; i < this._uploadImageList.length; i++) {
            this._uploadImageList[i].selected = EditConst.IMG_NOSELECT;
        }

        this._vue.$data.EnableUploadListTrashOK = false;
        this._vue.$data.Show.MyStampList = false;
        this._vue.$data.Show.TegakiStampList = false;
    }

    // #endregion

// #endregion

// #region ■内部関数

    /**
     * 保存対象のアップロードファイル情報を抽出
     * @summary 現在のアップロードファイルリスト一覧をidb保存向けにセットする。
     */
    public setSaveUploadInfo(uploadList: uploadPhoto[], setUploadList: idbEdit.photo[]) {

        for (let i: number = 0; i < uploadList.length; i++) {
            if (uploadList[i].fileId && !uploadList[i].deleteFlg && !uploadList[i].isEmpty) {
                let setPhoto: idbEdit.photo = new idbEdit.photo();
                setPhoto.fileId = uploadList[i].fileId;
                setPhoto.fileName = uploadList[i].fileName;
                setPhoto.height = uploadList[i].height;
                setPhoto.width = uploadList[i].width;
                setPhoto.uploadDate = uploadList[i].uploadDate;

                // photo only.
                setPhoto.fileNameKey = uploadList[i].fileKey;
                setPhoto.deleteDate = uploadList[i].deleteDate;
                setPhoto.deleteFlg = uploadList[i].deleteFlg;
                setPhoto.isUsed = uploadList[i].isUsed;

                setUploadList.push(setPhoto);
            }
        }
    }

    /**
     * 引き剥がし対象のIDを抽出
     * @summary アップロード一覧から画像削除される際、Canvasに配置済みならそのfileIdを抽出する。
     */
    protected getRemoveTargetIds(): string[] {

        let removeIds: string[] = [];

        removeIds = Enumerable.from(this._uploadImageList)
            .where(row => row.reqDelete === true && row.isUsed === true)
            .select(row => row.fileId).distinct()
            .toArray();

        return removeIds;
    }

    /**
     * 画像削除依頼処理
     * @summary 一覧から選択画像を削除し、Canvasに配置済ならその配置も引きはがす。
     * @abstract
     */
    abstract requestRemove(this: UploadImageLogicClass, fileIds: string[]);

    /**
     * 画像削除後の処理
     * @summary 一覧の削除枠を詰めて空き枠を整理する。
     */
    protected afterRemove() {

        let delNum: number = 0;
        for (let i: number = 0; i < this._uploadImageList.length; i++) {
            if (this._uploadImageList[i].reqDelete) {
                this._uploadImageList[i].reqDelete = false;
                this._uploadImageList[i].deleteFlg = true;
                this._uploadImageList[i].deleteDate = new Date();
                this._uploadImageList[i].selected = EditConst.IMG_NOSELECT;
                delNum++;
            }
        }

        // 削除した分、使用可能枠を追加する
        let nextKey: number = Number(this._uploadImageList[this._uploadImageList.length - 1].key) + 1;
        for (let i: number = 0; i < delNum; i++) {
            const ulp = new uploadPhoto();
            ulp.key = nextKey.toString();
            nextKey++;
            ulp.isUsed = false;
            ulp.isEmpty = true;
            this._uploadImageList.push(ulp);
        }

        this._vue.$data.UploadListModeSelect = true;
        this._vue.$data.EnableUploadListTrashOK = false;

        // indexedDBの情報更新
        const photoList: idbEdit.photo[] = [];
        this.setSaveUploadInfo(this._uploadImageList, photoList);

        let rtn = this.writeStorage(photoList);

        if (rtn) {
            Logger.debug("アップロードファイル情報 iDBへ書き込み 正常終了");
        }
        else {
            Logger.error("アップロードファイル情報 iDBへ書き込み エラー終了");
        }
    }

    /**
     * 画像設定処理(本体) 
     * @summary 選択された画像をCanvasへ配置する（実際の処理）
     * @abstract
     */
    abstract setImageBody(this: UploadImageLogicClass, fileId: string, targetPhoto: uploadPhoto, nextFun: () => void);

    /**
     *（共通部分）スタンプ系の画像設定処理
     * @summary 選択された画像をCanvasへ配置する（MYと手書きは同じ処理なのでココで共通化している）
     */
    protected commonSetStampBody(fileId: string, targetPhoto: uploadPhoto, okNextFunc: () => void) {
        let that = this;

        if (fileId === null) {
            Logger.debug("アイテムが選択されていません");
            return;
        }

        // 上限チェック
        let count = this.UseCount();
        if (count >= this.PUT_MAX) {
            dialogUtil.createErrorDialog(`配置可能な上限(${this.PUT_MAX}件)を超えました。`, dialogUtil.closeErrorDialog);
            dialogUtil.closeLoading();
            return;
        }

        // ローディングダイアログ開始
        util.dialogUtil.showLoading();

        let target: uploadPhoto;
        if (targetPhoto) {
            target = targetPhoto;
        }
        else {
            // 対象画像情報取得
            target = util.relic.listFind(this._uploadImageList, (up: uploadPhoto) => {
                return up.fileId === fileId;
            }, true);
        }

        // スタンプの一覧を取得
        this.sonbpIfController.getItems([this.VIEW_ITEM_TYPE],
            (stampList: itemVenderProperty[]) => {

                // アイテム番号の採番
                let idx: number;
                if (stampList.length === 0) {
                    idx = 1;
                }
                else {
                    let idxTmp: number = -1;
                    for (let i: number = 0; i < stampList.length; i++) {
                        const idx: number = Number(stampList[i].idx);
                        if (idxTmp < idx) {
                            idxTmp = idx;
                        }
                    }
                    idx = idxTmp + 1;
                }

                // sonbpへ依頼
                that.callSonbpIfSetStamp(idx, target,
                    () => {
                        // 使用中のフラグを立てる
                        let targetId = target.fileId;
                        let stamp: uploadPhoto = util.relic.listFind(that._uploadImageList, (upimg: uploadPhoto) => { return upimg.fileId?.split('@')[0] === targetId });
                        if (stamp) {
                            stamp.isUsed = true;
                        }

                        // 正常終了の場合
                        if (okNextFunc) okNextFunc();
                        util.dialogUtil.closeLoading();
                    },
                    () => {
                        // 上記以外の場合
                        util.dialogUtil.closeLoading();
                        util.dialogUtil.createErrorDialog(
                            this._edctrl.editMode == EDIT_MODE_KIND.MYSTAMP ?
                                constDialogMessage.ERROR_EDIT_CREATE_MYSTAMP :
                                constDialogMessage.ERROR_EDIT_CREATE_TEGAKISTAMP,
                            () => {
                                util.dialogUtil.closeErrorDialog();
                            }
                        );
                    }
                );
            },
            () => { });
    }

    /**
     * 画像サムネイル取得
     * @summary アップロード一覧に表示するサムネイル画像を取得する。
     */
    public getThumbnail(fileId: string, width: number, height: number, fileObject?: File) {
        var defer = $.Deferred();

        Logger.debug("画像サムネイル取得=> fileId[" + fileId + "]");

        // パラメータ
        const param = "token=" + this._authToken + "&fileId=" + fileId;

        
        try {
            let setIdx: number = -1;
            // 編集の場合は、対象枠に設定
            for (let i: number = 0; i < this._uploadImageList.length; i++) {
                if (this._uploadImageList[i].fileId == fileId) {
                    this._uploadImageList[i].isEmpty = false;
                    if (fileObject) {
                        this._uploadImageList[i].fileName = fileObject?.name;
                    } else {
                        this._uploadImageList[i].fileName = "no_name";
                    }
                    this._uploadImageList[i].width = width;
                    this._uploadImageList[i].height = height;
                    //this.setImgSrc(i, data) -> base64でセットするとgtmの処理が重たくなるのでurlを指定する。
                    this._uploadImageList[i].thumbnailUrl = this.WEBAPI_URL_GETTHUMBNAIL + "?" + param;
                    setIdx = i;

                    Logger.debug("画像サムネイル成功 編集[" + i + "]", fileId);
                    break;
                }
            }

            if (setIdx == -1) {
                // 新規の場合は、空き枠に設定
                for (let i: number = 0; i < this._uploadImageList.length; i++) {
                    if (this._uploadImageList[i].isEmpty) {
                        this._uploadImageList[i].fileId = fileId;
                        this._uploadImageList[i].isEmpty = false;
                        this._uploadImageList[i].uploadDate = new Date();
                        if (fileObject) {
                            this._uploadImageList[i].fileName = fileObject?.name;
                        } else {
                            this._uploadImageList[i].fileName = "no_name";
                        }
                        this._uploadImageList[i].width = width;
                        this._uploadImageList[i].height = height;
                        this._uploadImageList[i].thumbnailUrl = this.WEBAPI_URL_GETTHUMBNAIL + "?" + param;

                        Logger.debug("画像サムネイル成功 新規[" + i + "]", fileId);
                        break;
                    }
                }
            }

            defer.resolve(fileId);

        } catch (e) {
            defer.reject(e);
        }

        return defer.promise();
    }

    /**
     * サムネイル画像を対象img要素に適用
     * @summary getThumbnailの内部共通部分
     */
    private setImgSrc(imgSrcIdx: number, thumnail: Blob) {
        const reader = new FileReader();
        const that = this;
        reader.onerror = function (): void {
            Logger.error("img要素への画像適用でエラー発生");
        };
        reader.onload = function (event: any): void {
            that._uploadImageList[imgSrcIdx].thumbnailUrl = reader.result.toString();
        };
        reader.readAsDataURL(thumnail);
    }

    /**
     * 画像削除ボタンの活性制御
     * @summary 一覧の削除選択フラグONされた画像あれば、削除実施ボタンを活性化する。
     */
    private enableUploadListImageTrashOK(): void {

        let reqDels = util.relic.listFind(this._uploadImageList, (p: uploadPhoto) => {
            return p.reqDelete === true;
        });

        this._vue.$data.EnableUploadListTrashOK = (reqDels !== undefined) ? true : false;
    }

    /**
     * 一覧をidbへ保存
     * @summary アップロード一覧情報をidbへ保存する
     * @abstract
     */
    abstract writeStorage(list: idbEdit.photo[]): boolean;

    /**
     * スタンプ配置
     * @summary SonbpIfへスタンプ配置を依頼する。
     * @abstract
     */
    abstract callSonbpIfSetStamp(idx: number, stampMaster: uploadPhoto, callbackOk: () => void, callbackNg: () => void): void;


    // #region ■ファイルアップロード関連

    /**
     * 有効なアップロードファイルであるかのチェック
     * @summary ファイルのサイズ・MIMEチェックを行う。
     * @abstract
     */
    abstract uploadFileValid(this: UploadImageLogicClass, file: File): string;

    /**
     * アップロード処理入口
     * @summary アップロードを１０件ずつまとめて実施する。アップロード成功したらサムネイル画像の取得を行う。
     */
    private uploadImageFileCover(fileList: File[]): es6promise.Promise<any> {

        let messageList: string[] = [];
        const that = this;

        return new es6promise.Promise(async function (resolve, reject) {
            let promises: any[] = [];
            let normalErr: boolean = false;
            let fatalErr: boolean = false;
            let fatalErrMsg: string;
            let uploadResult: UploadImageResponseEntity[] = [];

            Logger.log("アップロード画像ファイル数[" + fileList.length + "]");

            // アップロード画像ファイル数分繰り返し
            for (let i: number = 0; i < fileList.length; i++) {

                // 10個ずつ実行
                promises.push(that.uploadImageFileBody(fileList[i]));

                if (i % 10 == 9 || fileList.length <= i + 1) {

                    Logger.debug("アップロード 10個ずつ実行 開始");

                    await asyncWhenApply(promises).then(function () {

                        Logger.debug("アップロード 10個ずつ実行 終了");

                        const argDetails = arguments[0];
                        for (let n: number = 0; n < argDetails.length; n++) {
                            const resEntity: response.responseEntity = argDetails[n];
                            if (resEntity.result.code === 0) {
                                // 正常終了の場合
                                uploadResult.push(resEntity.data);
                                Logger.debug("アップロード成功[" + JSON.stringify(resEntity.data) + "]");
                            }
                            else {
                                // 上記以外の場合
                                messageList.push(resEntity.result.message);
                                Logger.error("アップロード失敗-1[" + resEntity.result.message + "]");
                            }
                        }
                        promises = [];
                    }).catch(function (result: response.responseEntity[]) {
                        Logger.error("アップロード失敗-2 [" + JSON.stringify(result) + "]");
                        normalErr = true;
                        if (result) {
                            for (let n: number = 0; n < result.length; n++) {
                                if (0 < result[n].result.code) {
                                    messageList.push("・" + result[n].result.message);
                                }
                                else {
                                    fatalErr = true;
                                    if (result[n].result.code === -200) {
                                        //fatalErrMsg = constDialogMessage.ERROR_SESSION_TIMEOUT;
                                        util.ajax.TransErrorPage();
                                    }
                                    else {
                                        fatalErrMsg = constDialogMessage.ERROR_SYSTEM;
                                    }
                                }
                            }
                        }
                    });
                }

                // 結果判定
                if (normalErr || fatalErr) {
                    Logger.error("アップロード失敗-3");
                    break;
                }
            }

            if (!normalErr && !fatalErr) {
                resolve(uploadResult);
            } else {
                Logger.error("アップロード失敗-4");
                if (!fatalErr) {
                    reject(messageList);
                }
                else {
                    util.dialogUtil.createErrorDialog(fatalErrMsg, function () {
                        util.dialogUtil.closeErrorDialog();
                        window.location.href = that._edctrl.RetailerConfig.returnTop;
                    });
                    reject(null);
                }
            }
        }).then((uploadResult: UploadImageResponseEntity[]) => {

            // アップロード画像のサムネイル取得
            return new es6promise.Promise(async function (resolve, reject) {
                let promises: any[] = [];
                let err: boolean = false;

                // アップロード画像ファイル数分繰り返し
                for (let i: number = 0; i < uploadResult.length; i++) {

                    // 10個ずつ実行
                    promises.push(that.getThumbnail(uploadResult[i].fileId, uploadResult[i].width, uploadResult[i].height, uploadResult[i].fileObject));
                    if (i % 10 == 9 || uploadResult.length <= i + 1) {
                        await asyncWhenApply(promises).then(function () {
                            Logger.log("アップロード画像のサムネイル取得 arguments[" + JSON.stringify(arguments) + "]");
                            promises = [];
                        }).catch(function () {
                            err = true;
                        });
                    }

                    // 結果判定
                    if (err) {
                        Logger.error("画像サムネイルダウンロード失敗");
                        break;
                    }
                }

                if (!err) {
                    Logger.debug("画像サムネイルダウンロード全成功");
                    resolve(messageList);
                } else {
                    Logger.error("画像サムネイルダウンロード失敗");
                    messageList.push(constDialogMessage.ERROR_EDIT_DL_PHOTO_THUMBNAIL);
                    reject(messageList);
                }
            });

        }).then((messageList: string[]) => {

            Logger.debug("アップロードファイル情報 iDBへ書き込み 開始");

            return new es6promise.Promise(async function (resolve, reject) {

                if (0 < that._uploadImageList.length) {
                    const setPhotoList: idbEdit.photo[] = [];

                    // 写真情報
                    that.setSaveUploadInfo(that._uploadImageList, setPhotoList);

                    var ret;
                    if (that._isPhotoFlg) {
                        ret = editStrage.writeStorageToPhoto(setPhotoList);
                    } else {
                        ret = editStrage.writeStorageToMyStamp(setPhotoList);
                    }

                    if (ret) {
                        Logger.debug("アップロードファイル情報 WebStorageへ書き込み 正常終了", messageList);
                        resolve(messageList);
                    }
                    else {
                        Logger.error("アップロードファイル情報 WebStorageへ書き込み エラー終了", messageList);
                        reject(messageList);
                    }



                } else {
                    Logger.debug("アップロードファイル情報 iDBへ書き込み 正常終了(0件)", messageList);
                    resolve(messageList);
                }
            });
        });
    }

    /**
     * ファイルアップロード処理(１ファイル分)
     * @summary MD5を生成する。
     */
    private uploadImageFileBody(file: File) {
        var defer = $.Deferred();

        // MD5算出
        calcCheckSum(file).then((md5: string) => {

            Logger.debug("MD5 [" + md5 + "]");

            // アップロード実行
            this.uploadImageFile(file, md5).then((resData: response.responseEntity) => {
                if (resData.result.code === 0) {
                    // 正常
                    const photoEntity: UploadImageResponseEntity = resData.data;
                    photoEntity.fileObject = file;
                    defer.resolve(resData);
                }
                else {
                    // 準正常
                    resData.result.message = file.name + "：" + resData.result.message;
                    defer.resolve(resData);
                }
            }).catch((err: response.responseEntity) => {
                defer.reject(err);
            });
        }).catch((err: Error) => {
            Logger.error(err);
            defer.reject(err);
        });

        return defer.promise();
    }

    /**
     * ファイルアップロード(本体)
     * @summary Formデータを組み立てる。
     */
    private uploadImageFile(file: File, md5: string): es6promise.Promise<response.responseEntity> {

        // パラメータ
        let fd: FormData = new FormData();
        fd.append("token", this._authToken);
        fd.append("MD5", md5);
        fd.append("fileobject", file);

        return this.commonUploadImageFileSend(this.WEBAPI_URL_UPLOADIMAGE, fd, file.name);
    }

    /**
     *（共通部分）ファイルアップロード送信実施
     * @summary ファイルアップロードを行う。（実際のPOST送信してるのはココ）
     */
    protected commonUploadImageFileSend(url: string, fd: FormData, fileName: string): es6promise.Promise<response.responseEntity> {

        return new es6promise.Promise((resolve, reject) => {

            let fatalResponse: response.responseEntity = new response.responseEntity();
            fatalResponse.result = new response.resultEntity();

            // 送信処理
            const xhr = new XMLHttpRequest();
            xhr.onerror = (event: ProgressEvent) => {
                Logger.error("xhr.onerror event[", event);
                reject();
            }
            xhr.onreadystatechange = (event: ProgressEvent) => {
                if (xhr.readyState == 4) {
                    // 通信完了
                    if (200 <= xhr.status && xhr.status < 300) {
                        // 2xx: Success (成功)
                        Logger.debug("ファイル送信 2xx: Success (成功) file[" + fileName + "] readyState[" + xhr.readyState + "] status[" + xhr.status + "]");

                        let response: response.responseEntity = JSON.parse(xhr.responseText);
                        if (response.result.code == 0) {
                            // 正常終了
                            resolve(response);
                        }
                        else if (0 < response.result.code) {
                            // 準正常終了(主にファイルチェックエラー)
                            Logger.debug("XMLHttpRequest code [" + response.result.code + "] msg[" + response.result.message + "] response[" + JSON.stringify(response) + "]");
                            resolve(response);
                        }
                        else {
                            // 上記以外の場合
                            Logger.error("XMLHttpRequest err code [" + response.result.code + "] msg[" + response.result.message + "] response[" + JSON.stringify(response) + "]");
                            //reject(response.result.message);
                            reject(response);
                        }

                    } else if (300 <= xhr.status && xhr.status < 400) {
                        // 3xx: Redirection (転送)
                        Logger.debug("ファイル送信 3xx: Redirection (転送) file[" + fileName + "] readyState[" + xhr.readyState + "] status[" + xhr.status + "]");
                    } else if (400 <= xhr.status && xhr.status < 500) {
                        // 4xx: Client Error (クライアントエラー)
                        Logger.debug("ファイル送信 4xx: Client Error (クライアントエラー) file[" + fileName + "] readyState[" + xhr.readyState + "] status[" + xhr.status + "]");
                        fatalResponse.result.code = xhr.status * -1;
                        fatalResponse.result.message = constDialogMessage.ERROR_SYSTEM;
                        reject(fatalResponse);
                    } else if (500 <= xhr.status && xhr.status < 600) {
                        // 5xx: Server Error (サーバエラー)
                        Logger.debug("ファイル送信 5xx: Server Error (サーバエラー) file[" + fileName + "]  readyState[" + xhr.readyState + "] status[" + xhr.status + "]");
                        fatalResponse.result.code = xhr.status * -1;
                        fatalResponse.result.message = constDialogMessage.ERROR_SYSTEM;
                        reject(fatalResponse);
                    } else {
                        // 例外：クライエントエラーとする
                        Logger.debug("例外 file[" + fileName + "] readyState[" + xhr.readyState + "] status[" + xhr.status + "]");
                        fatalResponse.result.code = xhr.status * -1;
                        fatalResponse.result.message = constDialogMessage.ERROR_SYSTEM;
                        reject(fatalResponse);
                    }
                } else {
                    // 通信中
                    Logger.debug("ファイル送信 通信中 file[" + fileName + "] readyState[" + xhr.readyState + "] status[" + xhr.status + "]");
                }
            }

            xhr.open("POST", url);
            xhr.send(fd);
        });
    }

    // #endregion

// #endregion

}
